import type { Cipher, Sealed } from "./cipher.js";

/** The parts of the offline copy (spec11 data model). */
export const TABLES = [
  "meta",
  "notes",
  "notebooks",
  "outbox",
  "notebookOutbox",
  "conflicts",
] as const;
export type Table = (typeof TABLES)[number];

/** One write: a value to store, or `undefined` to delete the key. */
export interface Write {
  table: Table;
  key: string;
  value: unknown;
}

export type Snapshot = Record<Table, Map<string, unknown>>;

/** Where the offline copy is kept between app starts. */
export interface Persistence {
  load: () => Promise<Snapshot>;
  /** Applied together, in order. */
  write: (writes: readonly Write[]) => Promise<void>;
  /** Deletes everything (FR15). */
  destroy: () => Promise<void>;
}

const emptySnapshot = (): Snapshot =>
  Object.fromEntries(TABLES.map((t) => [t, new Map<string, unknown>()])) as Snapshot;

/** Keeps values in memory: unit tests, and the raw layer under encryption in tests. */
export function memoryPersistence(): Persistence & { raw: Snapshot } {
  let raw = emptySnapshot();
  return {
    get raw() {
      return raw;
    },
    async load() {
      const copy = emptySnapshot();
      for (const table of TABLES) for (const [k, v] of raw[table]) copy[table].set(k, v);
      return copy;
    },
    async write(writes) {
      for (const { table, key, value } of writes) {
        if (value === undefined) raw[table].delete(key);
        else raw[table].set(key, value);
      }
    },
    async destroy() {
      raw = emptySnapshot();
    },
  };
}

/** FR4: every value is encrypted before it reaches `inner`; keys are random ids only. */
export function encryptedPersistence(inner: Persistence, cipher: Cipher): Persistence {
  return {
    async load() {
      const sealed = await inner.load();
      const plain = emptySnapshot();
      for (const table of TABLES) {
        for (const [key, value] of sealed[table]) {
          plain[table].set(key, JSON.parse(await cipher.decrypt(value as Sealed)));
        }
      }
      return plain;
    },
    async write(writes) {
      const sealed: Write[] = [];
      for (const w of writes) {
        sealed.push({
          ...w,
          value: w.value === undefined ? undefined : await cipher.encrypt(JSON.stringify(w.value)),
        });
      }
      await inner.write(sealed);
    },
    destroy: () => inner.destroy(),
  };
}

const DB_NAME = "ronin-offline";

const done = (request: IDBRequest | IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    if (request instanceof IDBTransaction) {
      request.oncomplete = () => resolve();
      request.onabort = request.onerror = () => reject(request.error);
    } else {
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    }
  });

/**
 * One connection for the whole app. A delete of the database waits for every open
 * connection to close, so a second one (or a forgotten one) would make sign-out hang.
 */
let opened: Promise<IDBDatabase> | null = null;
function open() {
  opened ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      for (const table of TABLES) {
        if (!db.objectStoreNames.contains(table)) db.createObjectStore(table);
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // Another delete or upgrade asks: step aside instead of blocking it.
      db.onversionchange = () => {
        db.close();
        opened = null;
      };
      resolve(db);
    };
    request.onerror = () => {
      opened = null;
      reject(request.error);
    };
  });
  return opened;
}

/** IndexedDB inside the app's WebView (private app storage, no backups: spec08). */
export function indexedDbPersistence(): Persistence {
  return {
    async load() {
      const db = await open();
      const snapshot = emptySnapshot();
      const tx = db.transaction([...TABLES], "readonly");
      for (const table of TABLES) {
        const cursor = tx.objectStore(table).openCursor();
        cursor.onsuccess = () => {
          const c = cursor.result;
          if (!c) return;
          snapshot[table].set(String(c.key), c.value);
          c.continue();
        };
      }
      await done(tx);
      return snapshot;
    },
    async write(writes) {
      if (writes.length === 0) return;
      const db = await open();
      const tx = db.transaction([...new Set(writes.map((w) => w.table))], "readwrite");
      for (const { table, key, value } of writes) {
        if (value === undefined) tx.objectStore(table).delete(key);
        else tx.objectStore(table).put(value, key);
      }
      await done(tx);
    },
    async destroy() {
      const current = opened;
      opened = null;
      if (current) (await current.catch(() => null))?.close();
      await done(indexedDB.deleteDatabase(DB_NAME));
    },
  };
}
